import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Bell, Cpu, Inbox as InboxIcon, Check, X as XIcon, MessageSquare, Sparkles, Radio, RadioTower, Flame, Calendar, Building2, Receipt } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { pushSupported, getExistingSubscription, subscribeToPush, unsubscribeFromPush, testPush } from "@/lib/push";

/**
 * Notification bell for the sidebar. Polls /api/notifications/summary every 30s,
 * surfaces:
 *   • Parked auto-triage decisions with inline Send-now / Skip
 *   • Fresh contact_replied events with Open thread / AI reply / Dismiss
 *   • Unread inbox count with a deep-link to /inbox
 */
export default function NotificationBell({ onRefresh }) {
  const [summary, setSummary] = useState({
    total: 0,
    pending_triage: [],
    pending_triage_total: 0,
    fresh_replies: [],
    fresh_replies_total: 0,
    fresh_bookings: [],
    fresh_bookings_total: 0,
    unread_inbox: 0,
  });
  const [busyId, setBusyId] = useState(null);
  const [open, setOpen] = useState(false);
  const [personal, setPersonal] = useState({ notifications: [], unread: 0 });
  const [personalSeenIds] = useState(() => new Set());
  const [personalInited, setPersonalInited] = useState(false);
  const [radarState, setRadarState] = useState({ supported: false, enabled: false, checking: true });
  const [radarBusy, setRadarBusy] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/summary");
      setSummary(data);
    } catch (_e) { /* silent */ }
    try {
      const { data: p } = await api.get("/notifications/personal");
      const list = p?.notifications || [];
      // Toast for new unseen notifications (skip first load)
      if (personalInited) {
        list.filter((n) => !n.seen_at && !personalSeenIds.has(n.id)).forEach((n) => {
          toast(n.title, { description: n.body || undefined, duration: 6000 });
        });
      }
      list.forEach((n) => personalSeenIds.add(n.id));
      setPersonal({ notifications: list, unread: p?.unread || 0 });
      setPersonalInited(true);
    } catch (_e) { /* silent */ }
  }, [personalInited, personalSeenIds]);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    const handleRefresh = () => load();
    window.addEventListener("peak:inbox-read", handleRefresh);
    window.addEventListener("focus", handleRefresh);
    return () => {
      clearInterval(t);
      window.removeEventListener("peak:inbox-read", handleRefresh);
      window.removeEventListener("focus", handleRefresh);
    };
  }, [load]);

  // Reply Radar state — resolves the local subscription + server-side config.
  const checkRadar = useCallback(async () => {
    if (!pushSupported()) { setRadarState({ supported: false, enabled: false, checking: false }); return; }
    try {
      const { data: cfg } = await api.get("/notifications/push/config");
      const sub = await getExistingSubscription();
      const perm = typeof Notification !== "undefined" ? Notification.permission : "default";
      setRadarState({
        supported: true,
        enabled: !!(sub && cfg?.enabled && perm === "granted"),
        permission: perm,
        checking: false,
      });
    } catch (_e) {
      setRadarState({ supported: true, enabled: false, checking: false });
    }
  }, []);
  useEffect(() => { checkRadar(); }, [checkRadar]);

  const toggleRadar = async () => {
    setRadarBusy(true);
    try {
      if (radarState.enabled) {
        await unsubscribeFromPush();
        toast.success("Reply Radar off");
      } else {
        await subscribeToPush();
        toast.success("Reply Radar armed — we'll ping you the moment a lead replies");
      }
      await checkRadar();
    } catch (e) {
      toast.error(e.message || "Radar toggle failed");
    } finally { setRadarBusy(false); }
  };
  const sendRadarTest = async () => {
    setRadarBusy(true);
    try {
      const r = await testPush();
      if (r.delivered > 0) toast.success(`Test ping delivered to ${r.delivered} device${r.delivered === 1 ? "" : "s"}`);
      else toast.error("No devices received the ping — try enabling Radar first");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    } finally { setRadarBusy(false); }
  };

  const approve = async (row) => {
    setBusyId(row.activity_id);
    try {
      const { data } = await api.post(`/contacts/${row.contact_id}/pending-triage/approve`, { activity_id: row.activity_id });
      toast.success(`Sent · enrolled in ${data.workflow_name}`);
      load();
      onRefresh?.();
      window.dispatchEvent(new Event("peak:triage-updated"));
    } catch (e) { toast.error(e.response?.data?.detail || "Approve failed"); }
    finally { setBusyId(null); }
  };
  const skip = async (row) => {
    setBusyId(row.activity_id);
    try {
      await api.post(`/contacts/${row.contact_id}/pending-triage/skip`, { activity_id: row.activity_id });
      toast.success("Skipped");
      load();
      onRefresh?.();
      window.dispatchEvent(new Event("peak:triage-updated"));
    } catch (e) { toast.error(e.response?.data?.detail || "Skip failed"); }
    finally { setBusyId(null); }
  };

  const gotoInbox = () => { setOpen(false); navigate("/inbox"); };
  const gotoDashboard = () => { setOpen(false); navigate("/dashboard"); };
  const openThread = (row, aiReply = false) => {
    if (!row.thread_key) return;
    setOpen(false);
    const q = new URLSearchParams({ thread: row.thread_key });
    if (aiReply) q.set("ai", "1");
    navigate(`/inbox?${q.toString()}`);
  };
  const dismissReply = async (row) => {
    setBusyId(row.activity_id);
    try {
      await api.post(`/notifications/replies/${row.activity_id}/dismiss`);
      setSummary((s) => ({
        ...s,
        fresh_replies: s.fresh_replies.filter((r) => r.activity_id !== row.activity_id),
        fresh_replies_total: Math.max(0, (s.fresh_replies_total || 0) - 1),
        total: Math.max(0, (s.total || 0) - 1),
      }));
    } catch (e) { toast.error(e.response?.data?.detail || "Dismiss failed"); }
    finally { setBusyId(null); }
  };

  const dismissBooking = async (row) => {
    setBusyId(row.appointment_id);
    try {
      await api.post(`/notifications/bookings/${row.appointment_id}/dismiss`);
      setSummary((s) => ({
        ...s,
        fresh_bookings: s.fresh_bookings.filter((b) => b.appointment_id !== row.appointment_id),
        fresh_bookings_total: Math.max(0, (s.fresh_bookings_total || 0) - 1),
        total: Math.max(0, (s.total || 0) - 1),
      }));
    } catch (e) { toast.error(e.response?.data?.detail || "Dismiss failed"); }
    finally { setBusyId(null); }
  };
  const openCalendar = () => { setOpen(false); navigate("/calendar"); };

  const badge = (summary.total || 0) + (personal.unread || 0);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell"
          className="relative w-full flex items-center gap-3 px-3 py-2.5 border border-transparent text-xs font-bold uppercase tracking-wider text-[var(--peak-ink)] hover:bg-white hover:border-[var(--peak-ink)] transition-colors"
        >
          <Bell className="w-4 h-4" strokeWidth={2.5} />
          <span>Notifications</span>
          {badge > 0 && (
            <span
              data-testid="notification-bell-badge"
              className="ml-auto bg-[var(--peak-accent)] text-white text-[10px] font-mono px-1.5 py-0.5 min-w-[20px] text-center"
            >
              {badge > 99 ? "99+" : badge}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="right"
        align="end"
        className="w-96 p-0 rounded-none border-2 border-[var(--peak-ink)] bg-white shadow-none"
        data-testid="notification-panel"
      >
        <div className="px-4 py-3 border-b border-[var(--peak-ink)] bg-[var(--peak-surface-alt)] flex items-center justify-between">
          <div>
            <div className="section-label">Notifications</div>
            <div className="font-heading font-bold text-lg tracking-tight leading-none mt-1">
              {badge === 0 ? "All clear" : `${badge} to review`}
            </div>
          </div>
          <Bell className="w-4 h-4 text-[var(--peak-accent)]" strokeWidth={2.5} />
        </div>

        {personal.notifications.length > 0 && (
          <div className="border-b border-[var(--peak-border)]" data-testid="personal-notifications-section">
            <div className="px-4 pt-3 pb-2 flex items-center justify-between">
              <div className="section-label">Personal</div>
              {personal.unread > 0 && (
                <button
                  type="button"
                  onClick={async () => { await api.post("/notifications/personal/dismiss", {}); load(); }}
                  data-testid="personal-mark-all"
                  className="text-[10px] font-mono uppercase tracking-widest text-[var(--peak-muted)] hover:text-[var(--peak-ink)]"
                >
                  Mark all read
                </button>
              )}
            </div>
            {personal.notifications.slice(0, 5).map((n) => {
              const iconMap = {
                note_mention: MessageSquare,
                workspace_added: Building2,
                expense_submitted: Receipt,
                expense_approved: Receipt,
                expense_rejected: Receipt,
                expense_reimbursed: Receipt,
              };
              const NotifIcon = iconMap[n.kind] || Building2;
              return (
              <button
                key={n.id}
                type="button"
                onClick={async () => {
                  if (!n.seen_at) { await api.post("/notifications/personal/dismiss", { ids: [n.id] }); }
                  if (n.href) navigate(n.href);
                  setOpen(false);
                  load();
                }}
                data-testid={`personal-notif-${n.id}`}
                className={`w-full text-left px-4 py-2.5 hover:bg-[var(--peak-surface-alt)] flex items-start gap-3 border-t border-[var(--peak-border)] ${
                  !n.seen_at ? "bg-[var(--peak-surface-alt)]" : ""
                }`}
              >
                <div className={`w-7 h-7 flex-none flex items-center justify-center ${
                  !n.seen_at ? "bg-[var(--peak-accent)] text-[var(--peak-accent-contrast)]" : "bg-[var(--peak-border)] text-[var(--peak-muted)]"
                }`}>
                  <NotifIcon className="w-3.5 h-3.5" strokeWidth={2.5} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading font-bold tracking-tight text-sm leading-tight text-[var(--peak-ink)]">
                    {n.title}
                  </div>
                  {n.body && (
                    <div className="text-xs text-[var(--peak-muted)] mt-0.5 leading-snug truncate">{n.body}</div>
                  )}
                </div>
              </button>
            );})}
          </div>
        )}

        {radarState.supported && (
          <div className="px-4 py-3 border-b border-[var(--peak-border)]" data-testid="reply-radar-section">
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 flex items-center justify-center ${radarState.enabled ? "bg-[#008A27] text-white" : "bg-[var(--peak-surface-alt)] text-[var(--peak-muted)]"}`}>
                {radarState.enabled ? <RadioTower className="w-4 h-4" strokeWidth={2.5} /> : <Radio className="w-4 h-4" strokeWidth={2.5} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="section-label leading-none">Reply Radar</div>
                <div className="text-xs font-bold uppercase tracking-widest leading-none mt-1">
                  {radarState.checking ? "Checking…" : radarState.enabled ? "Armed" : "Off"}
                </div>
              </div>
              <button
                onClick={toggleRadar}
                disabled={radarBusy || radarState.checking}
                data-testid="reply-radar-toggle"
                className={`text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 border-2 disabled:opacity-60 ${radarState.enabled ? "border-[var(--peak-ink)] hover:bg-black hover:text-white" : "bg-[var(--peak-accent)] text-white border-[var(--peak-accent)] hover:bg-black hover:border-black"}`}
              >
                {radarState.enabled ? "Disarm" : "Arm"}
              </button>
            </div>
            {radarState.enabled && (
              <button
                onClick={sendRadarTest}
                disabled={radarBusy}
                data-testid="reply-radar-test"
                className="mt-2 w-full text-[10px] font-bold uppercase tracking-widest underline hover:text-[var(--peak-accent)] disabled:opacity-60"
              >
                Send test ping →
              </button>
            )}
            {!radarState.enabled && radarState.permission === "denied" && (
              <div className="mt-2 text-[10px] text-[#E50000]">
                Notifications blocked in browser settings — allow them for this site to enable Radar.
              </div>
            )}
          </div>
        )}

        {summary.pending_triage_total > 0 && (
          <div className="px-4 py-3 border-b border-[var(--peak-border)]" data-testid="pending-triage-section">
            <div className="section-label inline-flex items-center gap-1 mb-2">
              <Cpu className="w-3 h-3 text-[#008A27]" /> Auto-triage awaiting review · {summary.pending_triage_total}
            </div>
            <ul className="space-y-2 max-h-[320px] overflow-y-auto tick-scroll pr-1">
              {summary.pending_triage.map((row) => {
                const conf = row.confidence;
                const confColor = conf == null ? ""
                  : conf >= 85 ? "bg-[#008A27] text-white"
                  : conf >= 55 ? "bg-[#F0AD00] text-black"
                  : "bg-[#E50000] text-white";
                const busy = busyId === row.activity_id;
                return (
                  <li key={row.activity_id} className="p-2 border border-[var(--peak-border)] hover:border-[var(--peak-ink)]" data-testid={`bell-row-${row.activity_id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      {conf != null && <span className={`text-[10px] font-mono px-1.5 py-0.5 ${confColor}`}>{conf}%</span>}
                      <span className="text-xs font-bold uppercase tracking-widest truncate flex-1">{row.contact_name}</span>
                    </div>
                    <div className="text-xs text-[var(--peak-muted)] truncate mt-0.5">→ {row.workflow_name}</div>
                    {row.reasoning && <div className="text-[10px] text-[var(--peak-muted)] italic truncate mt-0.5">“{row.reasoning}”</div>}
                    <div className="mt-2 flex gap-1">
                      <button
                        onClick={() => approve(row)}
                        disabled={busy}
                        data-testid={`bell-approve-${row.activity_id}`}
                        className="flex-1 bg-[#008A27] hover:bg-black text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 inline-flex items-center justify-center gap-1 disabled:opacity-60"
                      >
                        <Check className="w-3 h-3" /> Send now
                      </button>
                      <button
                        onClick={() => skip(row)}
                        disabled={busy}
                        data-testid={`bell-skip-${row.activity_id}`}
                        className="flex-1 border border-[var(--peak-ink)] hover:bg-black hover:text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 inline-flex items-center justify-center gap-1 disabled:opacity-60"
                      >
                        <XIcon className="w-3 h-3" /> Skip
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
            <button
              onClick={gotoDashboard}
              data-testid="bell-view-all-triage"
              className="mt-2 w-full text-[10px] font-bold uppercase tracking-widest underline hover:text-[var(--peak-accent)]"
            >
              View full triage feed →
            </button>
          </div>
        )}

        {summary.fresh_bookings_total > 0 && (
          <div className="px-4 py-3 border-b border-[var(--peak-border)]" data-testid="fresh-bookings-section">
            <div className="section-label inline-flex items-center gap-1 mb-2">
              <Calendar className="w-3 h-3 text-[#008A27]" /> New bookings · {summary.fresh_bookings_total}
            </div>
            <ul className="space-y-2 max-h-[240px] overflow-y-auto tick-scroll pr-1">
              {summary.fresh_bookings.map((row) => {
                const busy = busyId === row.appointment_id;
                const when = row.start ? new Date(row.start) : null;
                return (
                  <li key={row.appointment_id} className="p-2 border border-[var(--peak-border)] hover:border-[var(--peak-ink)]" data-testid={`bell-booking-${row.appointment_id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] font-mono px-1.5 py-0.5 bg-[#008A27] text-white">📅 BOOKED</span>
                      <span className="text-xs font-bold uppercase tracking-widest truncate flex-1">{row.contact_name}</span>
                    </div>
                    <div className="text-xs font-semibold truncate mt-1">{row.calendar_name}</div>
                    {when && (
                      <div className="text-[10px] text-[var(--peak-muted)] mt-0.5">
                        {when.toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                      </div>
                    )}
                    {row.notes && <div className="text-[10px] text-[var(--peak-muted)] italic truncate mt-0.5">“{row.notes}”</div>}
                    <div className="mt-2 flex gap-1">
                      <button
                        onClick={openCalendar}
                        data-testid={`bell-booking-open-${row.appointment_id}`}
                        className="flex-1 bg-[var(--peak-blue-deep)] hover:bg-black text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 inline-flex items-center justify-center gap-1"
                      >
                        Open calendar
                      </button>
                      <button
                        onClick={() => dismissBooking(row)}
                        disabled={busy}
                        data-testid={`bell-booking-dismiss-${row.appointment_id}`}
                        className="border border-[var(--peak-ink)] hover:bg-[#E50000] hover:text-white hover:border-[#E50000] text-[10px] font-bold uppercase tracking-widest px-2 py-1 inline-flex items-center gap-1 disabled:opacity-60"
                      >
                        <XIcon className="w-3 h-3" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {summary.fresh_replies_total > 0 && (
          <div className="px-4 py-3 border-b border-[var(--peak-border)]" data-testid="fresh-replies-section">
            <div className="section-label inline-flex items-center gap-1 mb-2">
              <MessageSquare className="w-3 h-3 text-[var(--peak-accent)]" /> Fresh replies · {summary.fresh_replies_total}
            </div>
            <ul className="space-y-2 max-h-[320px] overflow-y-auto tick-scroll pr-1">
              {summary.fresh_replies.map((row) => {
                const busy = busyId === row.activity_id;
                const score = row.contact_score;
                const scoreColor = score == null ? ""
                  : score >= 85 ? "bg-[#008A27] text-white"
                  : score >= 70 ? "bg-[#F0AD00] text-black"
                  : "bg-[var(--peak-muted)] text-white";
                const tier = row.hotness || "";
                const tierPill = tier === "HOT" ? { cls: "bg-[#E50000] text-white", label: "🔥 HOT", testId: "hotness-pill-hot" }
                  : tier === "WARM" ? { cls: "bg-[#F0AD00] text-black", label: "WARM", testId: "hotness-pill-warm" }
                  : tier === "COLD" ? { cls: "bg-[var(--peak-muted)] text-white", label: "COLD", testId: "hotness-pill-cold" }
                  : null;
                return (
                  <li key={row.activity_id} className={`p-2 border hover:border-[var(--peak-ink)] ${tier === "HOT" ? "border-[#E50000] bg-[#FFF3F0]" : "border-[var(--peak-border)]"}`} data-testid={`bell-reply-${row.activity_id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      {tierPill && <span data-testid={tierPill.testId} className={`text-[10px] font-mono px-1.5 py-0.5 ${tierPill.cls}`} title={row.hotness_reason || tier}>{tierPill.label}</span>}
                      {score != null && <span className={`text-[10px] font-mono px-1.5 py-0.5 ${scoreColor}`}>{score}</span>}
                      {row.from_workflow && (
                        <span className="text-[10px] font-mono px-1.5 py-0.5 bg-[var(--peak-blue-deep)] text-white" title={`Reply to ${row.workflow_name || 'a workflow'}`}>
                          {row.workflow_name ? `↩ ${row.workflow_name}`.slice(0, 24) : "↩ WORKFLOW"}
                        </span>
                      )}
                      <span className="text-xs font-bold uppercase tracking-widest truncate flex-1">{row.contact_name}</span>
                    </div>
                    {row.subject && <div className="text-xs font-semibold truncate mt-1">{row.subject}</div>}
                    {row.preview && <div className="text-[10px] text-[var(--peak-muted)] italic truncate mt-0.5">“{row.preview}”</div>}
                    {row.hotness_reason && tier === "HOT" && (
                      <div className="text-[10px] text-[#E50000] font-semibold mt-0.5 flex items-center gap-1">
                        <Flame className="w-2.5 h-2.5" /> {row.hotness_reason}
                      </div>
                    )}
                    <div className="mt-2 flex gap-1">
                      {row.thread_key && (
                        <button
                          onClick={() => openThread(row, true)}
                          disabled={busy}
                          data-testid={`bell-reply-ai-${row.activity_id}`}
                          className="flex-1 bg-[var(--peak-blue-deep)] hover:bg-black text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 inline-flex items-center justify-center gap-1 disabled:opacity-60"
                        >
                          <Sparkles className="w-3 h-3" /> AI reply
                        </button>
                      )}
                      {row.thread_key && (
                        <button
                          onClick={() => openThread(row, false)}
                          disabled={busy}
                          data-testid={`bell-reply-open-${row.activity_id}`}
                          className="flex-1 border border-[var(--peak-ink)] hover:bg-black hover:text-white text-[10px] font-bold uppercase tracking-widest px-2 py-1 inline-flex items-center justify-center gap-1 disabled:opacity-60"
                        >
                          Open
                        </button>
                      )}
                      <button
                        onClick={() => dismissReply(row)}
                        disabled={busy}
                        data-testid={`bell-reply-dismiss-${row.activity_id}`}
                        className="border border-[var(--peak-ink)] hover:bg-[#E50000] hover:text-white hover:border-[#E50000] text-[10px] font-bold uppercase tracking-widest px-2 py-1 inline-flex items-center justify-center gap-1 disabled:opacity-60"
                        title="Mark as seen without opening"
                      >
                        <XIcon className="w-3 h-3" />
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {summary.unread_inbox > 0 && (
          <div className="px-4 py-3 border-b border-[var(--peak-border)]" data-testid="unread-inbox-section">
            <button
              onClick={gotoInbox}
              data-testid="bell-open-inbox"
              className="w-full flex items-center gap-3 hover:bg-[var(--peak-surface-alt)] p-2 -m-2"
            >
              <InboxIcon className="w-4 h-4 text-[var(--peak-blue-deep)]" strokeWidth={2.5} />
              <div className="flex-1 text-left">
                <div className="section-label">Inbox</div>
                <div className="text-xs font-bold uppercase tracking-widest">
                  {summary.unread_inbox} unread repl{summary.unread_inbox === 1 ? "y" : "ies"}
                </div>
              </div>
              <span className="text-[10px] font-mono bg-[var(--peak-accent)] text-white px-1.5 py-0.5">{summary.unread_inbox}</span>
            </button>
          </div>
        )}

        {badge === 0 && (
          <div className="px-4 py-8 text-center" data-testid="bell-empty-state">
            <div className="section-label text-[var(--peak-muted)]">You&apos;re up to date.</div>
            <div className="text-xs text-[var(--peak-muted)] mt-1">New pending decisions and unread replies will appear here.</div>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
